import React, { useEffect, useState } from 'react'
import { Container, Row, Col, Card, Button } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import useAuth from '../../context/auth/useAuth'
import { useDispatch, useSelector } from 'react-redux'
import { editCandidateMethodologies, getCandidate } from '../../actions/candidatesActions'
import methodologies from '../../components/methodologies.json'
import { DndContext, KeyboardSensor, PointerSensor, TouchSensor, closestCorners, useSensor, useSensors } from '@dnd-kit/core'
import {arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { MethodologyContainer } from '../../components/methodologies/MethodologyContainer'
import Swal from 'sweetalert2'
import SubMenuHabilities from '../../components/menus/SubMenuHabilities'
import { useParams } from 'react-router-dom'

export default function Methodologies() {
    const {id: candidateId} = useParams() || {}
    const { t } = useTranslation()
	const {client} = useAuth()
    const dispatch = useDispatch()

    const {candidate, resultcandidate} = useSelector(state=>state.candidates)

    const [methodologiesOptions, setMethodologiesOptions] = useState({
        initial: methodologies,
        selected: []
    })

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(TouchSensor),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
    )

    function findContainer(id) {
        if (id in methodologiesOptions) {
            return id;
        }

        return Object.keys(methodologiesOptions).find((key) => methodologiesOptions[key].find(k => k.id === id));
    }

    const handleDragEnd = (event) => {
        const { active, over } = event;
        const { id } = active;
        const { id: overId } = over;

        const activeContainer = findContainer(id);
        const overContainer = findContainer(overId);

        if (!activeContainer || !overContainer || activeContainer !== overContainer) {
            return;
        }

        const activeIndex = methodologiesOptions[activeContainer].findIndex(item => item.id === active.id);
        const overIndex = methodologiesOptions[overContainer].findIndex(item => item.id === overId);

        if (activeIndex !== overIndex) {
            setMethodologiesOptions((items) => ({
                ...items,
                [overContainer]: arrayMove(items[overContainer], activeIndex, overIndex)
            }));
        }
    }

    const handleDragOver = (event) => {
        const { active, over } = event;

        const { id } = active;
        const { id: overId } = over;

        // Find the containers
        const activeContainer = findContainer(id);
        const overContainer = findContainer(overId);

        if (!activeContainer || !overContainer || activeContainer === overContainer) {
            return;
        }

        setTimeout(() => {
            setMethodologiesOptions((prev) => {
                const activeMethodologies = prev[activeContainer];
                const overMethodologies = prev[overContainer];

                // Find the indexes for the items
                const activeIndex = activeMethodologies.findIndex(item => item.id === id);
                const overIndex = overMethodologies.findIndex(item => item.id === overId);

                let newIndex;
                if (overId in prev) {
                    // We're at the root droppable of a container
                    newIndex = overMethodologies.length + 1;
                } else {
                    const isBelowLastItem =
                    over &&
                    overIndex === overMethodologies.length - 1;

                    const modifier = isBelowLastItem ? 1 : 0;

                    newIndex = overIndex >= 0 ? overIndex + modifier : overMethodologies.length + 1;
                }

                return {
                    ...prev,
                    [activeContainer]: [
                    ...prev[activeContainer].filter((item) => item.id !== active.id)
                    ],
                    [overContainer]: [
                        ...prev[overContainer].slice(0, newIndex),
                        methodologiesOptions[activeContainer][activeIndex],
                        ...prev[overContainer].slice(newIndex, prev[overContainer].length)
                    ]
                };
            });
        }, 5)
    }

    const handleEditMethodologies = async() => {
        if (methodologiesOptions.selected.length > 7) {
            Swal.fire({
                showConfirmButton: true,
                icon: 'warning',
                text: t('profile.candidate.maxMethodologiesExceeded')
            })
        } else if (methodologiesOptions.selected.length < 3) {
            Swal.fire({
                showConfirmButton: true,
                icon: 'warning',
                text: t('profile.candidate.minMethodologiesNotReached')
            })
        } else {
            let methodologiesToSend = []

            for await (const competence of methodologiesOptions.selected) {
                methodologiesToSend.push(competence.id)
            }
            dispatch(editCandidateMethodologies(candidateId || client?.id, methodologiesToSend))
        }
    }

    useEffect(() => {
        if(client){
            dispatch(getCandidate(candidateId || client?.id))
        }
    },[dispatch, t, client])

    useEffect( () => {
        async function setMehodologies(){
            if (candidate && candidate.methodologies) {
                let userMethodologiesUsed = []
                let userInitialMethodologies = [...methodologies]

                for await(const competence of candidate.methodologies) {
                    if (methodologies.find(c => c.id === competence)) {
                        userMethodologiesUsed.push(methodologies.find(c => c.id === competence))
                        userInitialMethodologies.splice(userInitialMethodologies.findIndex(c => c.id === competence), 1)
                    }
                }

                setMethodologiesOptions({
                    initial: userInitialMethodologies,
                    selected: userMethodologiesUsed
                })
            }
        }
        setMehodologies()
    }, [candidate])

    useEffect(() => {
        if(resultcandidate){
            switch (resultcandidate) {
                case 'successEditMethodologies':
                    dispatch(getCandidate(candidateId || client?.id))
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('profile.candidate.successEditMethodologies')
                    })
                    break;
                case 'errorEditMethodologies':
                    Swal.fire({
                        showConfirmButton: true,
                        icon: 'warning',
                        text: t('profile.candidate.errorEditMethodologies')
                    })
                    break;
                default:
                    break;
            }
        }
    // eslint-disable-next-line
    }, [resultcandidate])

    return (
        <>
            <Container className='cuerpo'>
                <Row className='mb-4'>
                    <Col className='d-flex align-items-center'>
                        <img src="/images/habilidadesbig.svg" alt="Icono Habilidades" />
                        <h1 className='ms-2 m-0'>{t('menus.skills')}</h1>
                    </Col>
                </Row>
                <Row className="mt-2">
                    <Col>
                        <Card className='mb-5 sombra lateral' >
                            <Card.Body className='p-0 d-flex'>
                                <div className='d-none d-sm-block submenu-lateral'>
                                        <SubMenuHabilities seccion='methodologies' usertype={candidateId ? 'Superadmin' : 'Candidate'} candidateId={candidateId}/>
                                </div>
                                <div className='px-5 py-3 contenido-datos'>
                                    <Row className="mt-5 mb-3">
                                        <Col>
                                            <h3>{t('profile.candidate.methodologies')}</h3>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            {t('profile.candidate.selectMethodologiesDescription')}
                                        </Col>
                                    </Row>
                                    <Row className='my-5'>
                                        <DndContext sensors={sensors} onDragOver={handleDragOver} onDragEnd={handleDragEnd} collisionDetection={closestCorners}>
                                            <Col xs={6}>
                                                <MethodologyContainer id='initial' items={methodologiesOptions.initial} title="availableMethodologies"/>
                                            </Col>
                                            <Col xs={6}>
                                                <MethodologyContainer id='selected' items={methodologiesOptions.selected} title="chosenMethodologies"/>
                                            </Col>
                                        </DndContext>
                                    </Row>
                                    <Row className="my-3 d-flex justify-content-end">
                                        <Col xs='12' lg='5'>
                                            <Button type='button' variant='primary' className="w-100 my-2" onClick={() => {handleEditMethodologies()}}>{t('profile.candidate.editMethodologies')}</Button>
                                        </Col>
                                    </Row>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    )
}